import React, { useEffect, useState, useRef } from "react";
import Menu from "@mui/material/Menu";
import {
  Table,
  TableProps,
  Form,
  Popconfirm,
  Input,
  Space,
  Typography as Typographys,
  Tag,
  Select,
  notification,
} from "antd";
import type { InputRef, TableColumnType } from "antd";
import {
  UploadOutlined,
  DeleteOutlined,
  EditOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import Button from "@mui/material/Button";
import { Button as Buttons } from "antd";
import Box from "@mui/material/Box";
import { styled } from "@mui/material/styles";
import InvoiceController from "./Controller/invoice_controller.ts";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Checkbox from "@mui/material/Checkbox";
import Stack from "@mui/material/Stack";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import { PDFDownloadLink } from "@react-pdf/renderer";
import MyDocument from "./pdf/event-invoice.tsx";
import CustomSeparator from "../../layouts/AdminLayout/Header/Breadcrumbs.js";
import { Typography } from "@mui/material";
import { createStyles } from "antd-style";
import type { FilterDropdownProps } from "antd/es/table/interface";
import Highlighter from "react-highlight-words";

const useStyle = createStyles(({ css }) => {
  return {
    customTable: css`
      .ant-table {
        .ant-table-container {
          .ant-table-body,
          .ant-table-content {
            scrollbar-width: thin;
            scrollbar-color: #eaeaea transparent;
            scrollbar-gutter: stable;
          }
        }
      }
    `,
  };
});

const { TextArea } = Input;
const { Text } = Typographys;
type NotificationType = "success" | "info" | "warning" | "error";

type TableRowSelection<T extends object = object> =
  TableProps<T>["rowSelection"];

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

interface DataType {
  paymentID: string;
  event_name: string;
  email: string;
  payment_datetime_created: string;
  payment_date_created: string;
  registered_address: string;
  payment_contact_name: string;
  memberID: string;
  invoice_no: string;
  payment_status: string;
  member_status: string;
  event_registrationID: string;
  beneficiary: any;
  registration_start_date: string;
  registration_end_date: any;
  registration_price: any;
  early_bird_start_date: string;
  early_bird_end_date: string;
  early_bird_price: any;
  company_name: string;
}

type DataIndex = keyof DataType;

interface EditableCellProps extends React.HTMLAttributes<HTMLElement> {
  editing: boolean;
  dataIndex: string;
  title: any;
  inputType:
    | "number"
    | "text"
    | "date"
    | "select"
    | "select_payment"
    | "payment_remark"
    | "individual_previous_employment_period_1"
    | "individual_previous_employment_designation_1"
    | "individual_previous_employment_period_2"
    | "individual_previous_employment_designation_2"
    | "individual_academic_qualification";
  record: DataType;
  index: number;
  options: any;
}

const EditableCell: React.FC<React.PropsWithChildren<EditableCellProps>> = ({
  editing,
  dataIndex,
  title,
  inputType,
  record,
  index,
  children,
  options,
  ...restProps
}) => {
  // Determine the input node based on inputType
  let inputNode: any;
  if (inputType === "date") {
    inputNode = <Input type="date" />;
  } else if (inputType === "select_payment") {
    let disabled: any;
    if (record.payment_status == null) {
      disabled = true;
    }
    inputNode = (
      <Select
        style={{ marginBottom: 2 }}
        disabled={disabled}
        value={record.payment_status}
      >
        {options.map((option) => (
          <Select.Option key={option.value} value={option.value}>
            {option.label}
          </Select.Option>
        ))}
      </Select>
    );
  } else {
    inputNode = <TextArea autoSize={{ minRows: 2, maxRows: 10 }} />;
  }

  return (
    <td {...restProps}>
      {editing ? (
        <Form.Item
          name={dataIndex}
          style={{ margin: 0 }}
          rules={[
            {
              required: true,
              message: `Please Input ${title}!`,
            },
          ]}
        >
          {inputNode}
        </Form.Item>
      ) : (
        children
      )}
    </td>
  );
};

const CheckboxList = ({ baseColumns, checkedColumns, onChange }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleToggle = (value: Number) => () => {
    const currentIndex = checkedColumns.indexOf(value);
    const newChecked = [...checkedColumns];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    onChange(newChecked); // Call the parent onChange handler
  };

  return (
    <>
      <Button
        id="demo-positioned-button"
        aria-controls={open ? "demo-positioned-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={handleClick}
        variant="contained"
        style={{ backgroundColor: "#d33333" }}
      >
        Columns
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          style: {
            // maxHeight: 400,
            width: "20ch",
          },
        }}
      >
        <List
          sx={{ width: "100%", maxWidth: 360, bgcolor: "background.paper" }}
        >
          {baseColumns.map((value) => {
            const isChecked = checkedColumns.includes(value.dataIndex);

            const labelId = `checkbox-list-label-${value.dataIndex}`;
            return (
              <ListItem key={value.dataIndex} disablePadding>
                <ListItemButton
                  role={undefined}
                  onClick={handleToggle(value.dataIndex)}
                  dense
                >
                  <ListItemIcon>
                    <Checkbox
                      edge="start"
                      checked={isChecked}
                      tabIndex={-1}
                      disableRipple
                      inputProps={{ "aria-labelledby": labelId }}
                    />
                  </ListItemIcon>
                  <ListItemText id={labelId} primary={value.title} />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Menu>
    </>
  );
};

function App() {
  const [api, contextHolder] = notification.useNotification();

  const openNotificationWithIcon = (
    type: NotificationType,
    message: String
  ) => {
    api[type]({
      message: "Success",
      description: message,
    });
  };

  const [rows, setRows] = useState<any[]>([]);
  const [data, setData] = useState<DataType[]>(rows);

  useEffect(() => {
    InvoiceController.getEventInvoice().then((response) => {
      console.log(response);

      if (Array.isArray(response)) {
        const dynamicRows = response.map((item, index) => {
          const date = item.payment_datetime_created;
          const dateOnly = date ? date.split(" ")[0] : null; // Outputs '2024-10-07'
          const beneficiaryData = item.beneficiary;

          const companyObject = beneficiaryData.find(
            (item) => item.company_name
          );
          const company_name = companyObject
            ? companyObject.company_name
            : null;

          return {
            key: index,
            paymentID: item.paymentID,
            event_registrationID: item.event_registrationID,
            payment_status: item.payment_status,
            payment_remark: item.payment_remark,
            payment_datetime_created: item.datetime_created,
            payment_date_created: dateOnly,
            payment_contact_name: item.contact_name,
            invoice_no: item.invoice_no,
            event_name: item.event_name,
            event_desc: item.event_desc,
            event_venue: item.event_venue,

            registration_start_date: item.registration_start_date,
            registration_end_date: item.registration_end_date,
            registration_price: item.event_fee.normal_rate,

            early_bird_start_date: item.early_bird_start_date,
            early_bird_end_date: item.early_bird_end_date,
            early_bird_price: item.event_fee.early_bird_rate,
            event_start_datetime: item.event_start_datetime,
            event_end_datetime: item.event_end_datetime,
            beneficiary: item.beneficiary,
            company_name: company_name,
          };
        });

        setRows(Array.from(dynamicRows));
      } else {
      }
    });
  }, []);

  // Update data whenever rows changes
  useEffect(() => {
    setData(rows);
  }, [rows]);

  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [editingKey, setEditingKey] = useState("");

  const isEditing = (record: DataType) => {
    return String(record.event_registrationID) === String(editingKey);
  };
  // Number(record.paymentID) === Number(editingKey);
  const edit = (
    record: Partial<DataType> & { event_registrationID: string }
  ) => {
    form.setFieldsValue({ name: "", age: "", address: "", ...record });
    setEditingKey(record.event_registrationID);
  };

  const cancel = () => {
    setEditingKey("");
  };

  type ApiResponse = {
    data: any[];
    status: string;
  };

  const refreshData = ({ status, data }: ApiResponse) => {
    const dynamicRows = data.map((item, index) => {
      const date = item.payment_datetime_created;
      const dateOnly = date.split(" ")[0]; // Outputs '2024-10-07'
      const beneficiaryData = item.beneficiary;
      const companyObject = beneficiaryData.find((item) => item.company_name);
      const company_name = companyObject ? companyObject.company_name : null;

      return {
        key: index,
        paymentID: item.paymentID,
        event_registrationID: item.event_registrationID,
        payment_status: item.payment_status,
        payment_remark: item.payment_remark,
        payment_datetime_created: item.datetime_created,
        payment_date_created: dateOnly,
        payment_contact_name: item.contact_name,
        invoice_no: item.invoice_no,
        event_name: item.event_name,
        event_desc: item.event_desc,
        event_venue: item.event_venue,

        registration_start_date: item.registration_start_date,
        registration_end_date: item.registration_end_date,
        registration_price: item.event_fee.normal_rate,

        early_bird_start_date: item.early_bird_start_date,
        early_bird_end_date: item.early_bird_end_date,
        early_bird_price: item.event_fee.early_bird_rate,
        event_start_datetime: item.event_start_datetime,
        event_end_datetime: item.event_end_datetime,
        beneficiary: item.beneficiary,
        company_name: company_name,
      };
    });

    setRows(Array.from(dynamicRows));
  };
  const save = async (key: React.Key) => {
    try {
      const row = (await form.validateFields()) as DataType;
      const newData = [...data];
      const index = newData.findIndex((item) => key === item.paymentID);
      const paymentID = newData[index].paymentID;
      const memberID = newData[index].memberID;
      if (index > -1) {
        // set rows here
        const payload = {
          paymentID: paymentID,
          memberID: memberID,
          data: row,
        };

        InvoiceController.editPayment(payload).then((response) => {
          if (response?.status === "success") {
            openNotificationWithIcon(
              "success",
              "Your data has been saved successfully!"
            );
            refreshData(response);
          }
        });
        setEditingKey("");
      } else {
        // newData.push(row);
        setData(newData);
        setEditingKey("");
      }
    } catch (errInfo) {
      console.log("Validate Failed:", errInfo);
    }
  };

  // **************************** SEARCH FILTER ANTD TABLE *****************************************************
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const searchInput = useRef<InputRef>(null);

  const handleSearch = (
    selectedKeys: string[],
    confirm: FilterDropdownProps["confirm"],
    dataIndex: DataIndex
  ) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters: () => void) => {
    clearFilters();
    setSearchText("");
  };

  const getColumnSearchProps = (
    dataIndex: DataIndex
  ): TableColumnType<DataType> => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() =>
            handleSearch(selectedKeys as string[], confirm, dataIndex)
          }
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Buttons
            type="primary"
            onClick={() =>
              handleSearch(selectedKeys as string[], confirm, dataIndex)
            }
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Buttons>
          <Buttons
            onClick={() => clearFilters && handleReset(clearFilters)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Buttons>
          <Buttons
            type="link"
            size="small"
            onClick={() => {
              confirm({ closeDropdown: false });
              setSearchText((selectedKeys as string[])[0]);
              setSearchedColumn(dataIndex);
            }}
          >
            Filter
          </Buttons>
          <Buttons
            type="link"
            size="small"
            onClick={() => {
              close();
            }}
          >
            close
          </Buttons>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex]
            .toString()
            .toLowerCase()
            .includes((value as string).toLowerCase())
        : "",
    onFilterDropdownOpenChange: (open) => {
      if (open) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });
  // ************************************************************************************************************

  const baseColumns = [
    {
      title: "Payment ID",
      dataIndex: "paymentID",
      key: "paymentID",
      ...getColumnSearchProps("paymentID"),
    },
    {
      title: "Company Name",
      dataIndex: "company_name",
      key: "company_name",
      ...getColumnSearchProps("company_name"),
    },
    {
      title: "Event Name",
      dataIndex: "event_name",
      key: "event_name",
      width: 250,
      ...getColumnSearchProps("event_name"),
    },
    {
      title: "Date",
      dataIndex: "payment_date_created",
      key: "payment_date_created",
      width: 150,
      ...getColumnSearchProps("payment_date_created"),
    },
    {
      title: "Invoice No.",
      dataIndex: "invoice_no",
      key: "invoice_no",
      ...getColumnSearchProps("invoice_no"),
      render: (data: string) => {
        return <Text>{data}</Text>;
      },
    },
    {
      title: "Payment Status",
      dataIndex: "payment_status",
      key: "payment_status",
      editable: true,
      render: (payment_status: any, record: DataType) => {
        if (payment_status === 1) {
          return (
            <>
              <Space>
                <Text>
                  <Tag color="green">Paid</Tag>
                </Text>
              </Space>
            </>
          );
        } else if (payment_status === 0) {
          return (
            <>
              <Space>
                <Text>
                  <Tag color="red">Pending</Tag>
                </Text>
              </Space>
            </>
          );
        }
      },
      filters: [
        { text: "Paid", value: 1 },
        { text: "Pending", value: 0 },
        { text: "Unpaid", value: 2 },
      ],
      onFilter: (value, record) => record.payment_status === value,
    },
    {
      title: "operation",
      dataIndex: "operation",
      width: 150,
      render: (_: any, record: DataType) => {
        const editable = isEditing(record);

        const data = {
          paymentID: record.paymentID || "",
          registration_start_date: record.registration_start_date || "",
          registration_end_date: record.registration_end_date || "",
          registration_price: record.registration_price || 0,
          early_bird_price: record.early_bird_price || 0,
          early_bird_start_date: record.early_bird_start_date || "",
          early_bird_end_date: record.early_bird_end_date || "",
          date: record.payment_date_created || "",
          date_time: record.payment_datetime_created || "",
          invoice_no: record.invoice_no || 0,
          event_name: record.event_name || "",
          beneficiary: record.beneficiary ? record.beneficiary : [],
        };
        return (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              width: "90%",
            }}
          >
            <PDFDownloadLink
              document={<MyDocument data={data} />}
              fileName={`invoice-${record.paymentID}.pdf`}
            >
              {({ loading }) =>
                loading ? (
                  "Generating PDF..."
                ) : (
                  <Buttons
                    color="default"
                    variant="solid"
                    icon={<ReceiptLongIcon />}
                    style={{ marginBottom: "10px", textDecoration: "none" }}
                  >
                    Invoice
                  </Buttons>
                )
              }
            </PDFDownloadLink>

            {editable ? (
              <>
                <Buttons
                  onClick={() => save(record.paymentID)}
                  color="default"
                  variant="solid"
                  style={{ marginBottom: "10px" }}
                >
                  Save
                </Buttons>
                <Buttons color="default" variant="outlined">
                  <Popconfirm title="Sure to cancel?" onConfirm={cancel}>
                    Cancel
                  </Popconfirm>
                </Buttons>
              </>
            ) : (
              <>
                <Buttons
                  color="default"
                  variant="solid"
                  icon={<EditOutlined />}
                  disabled={editingKey !== ""}
                  onClick={() => edit(record)}
                  style={{ marginBottom: "10px" }}
                >
                  Edit
                </Buttons>
              </>
            )}
          </div>
        );
      },
    },
  ];

  const onSelectChange = (
    newSelectedRowKeys: React.Key[],
    selectedRows: DataType[]
  ) => {
    console.log("selectedRowKeys changed: ", newSelectedRowKeys);
    setSelectedRowKeys(newSelectedRowKeys);

    // call api here
  };

  const rowSelection: TableRowSelection<DataType> = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  const [checkedColumns, setCheckedColumns] = useState([
    "paymentID",
    "event_name",
    "company_name",
    "email",
    "payment_date_created",
    "payment_status",
    "invoice_no",
    "member_status",
    "operation",
  ]);

  const onChange = (checkedValues) => {
    setCheckedColumns(checkedValues);
  };

  const paymentStatusOptions = [
    { label: "Unpaid", value: 0 },
    { label: "Paid", value: 1 },
  ];
  // Create mergedColumns directly using baseColumns
  const mergedColumns = baseColumns
    .filter(
      (col) =>
        typeof col.dataIndex === "string" &&
        checkedColumns.includes(col.dataIndex)
    )
    .map((col) => {
      if (!col.editable) {
        return col;
      }
      return {
        ...col,
        onCell: (record: DataType) => ({
          record,
          inputType:
            col.dataIndex === "payment_status" ? "select_payment" : "text", // Update to set 'date' for date columns
          dataIndex: col.dataIndex,
          title: col.title,
          editing: isEditing(record),
          options:
            col.dataIndex === "payment_status"
              ? paymentStatusOptions
              : undefined,
        }),
      };
    });
  const { styles } = useStyle();

  return (
    <>
      <Box
        sx={{
          display: "flex",
          backgroundColor: "#e8e8e8",
          height: "auto", // scroll y for page
          minHeight: "150vh",
          width: "100vw", // Full viewport width
          overflow: "hidden", // Ensure no overflow from the Box itself
        }}
      >
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 3,
            display: "flex", // Ensures the table fills the remaining space
            flexDirection: "column",
            height: "100%", // Fill remaining height
            width: "100%", // Fill remaining width
          }}
        >
          <DrawerHeader />
          <CustomSeparator pageName="invoice" />
          {contextHolder}

          <Typography
            gutterBottom
            variant="h4"
            sx={{ color: "text.secondary" }}
          >
            Event Invoice Directory
          </Typography>
          <Stack spacing={2} direction="row" sx={{ mb: 3 }}>
            <CheckboxList
              baseColumns={baseColumns}
              checkedColumns={checkedColumns}
              onChange={onChange}
            />
          </Stack>

          {/* Box to hold the table with full width/height */}
          <Box
            sx={{
              flexGrow: 1, // Ensures the table box expands
              backgroundColor: "#fff", // Set the background color for the table container
              height: "100%", // Ensures the table fills available height
              width: "100%", // Ensures the table fills available width
              overflow: "auto", // Allow scrolling if needed
              borderRadius: "8px", // Optional: for rounded corners
            }}
          >
            <Form form={form} component={false}>
              <Table<DataType>
                className={styles.customTable}
                components={{
                  body: { cell: EditableCell },
                }}
                bordered
                columns={mergedColumns}
                dataSource={rows}
                rowSelection={rowSelection}
                rowKey="event_registrationID" // Set your custom key here
                scroll={{ y: 200 * 5, x: "max-content" }}
              />
            </Form>
          </Box>
        </Box>
      </Box>
    </>
  );
}

export default App;
